import { useState } from "react";
import { toast } from "sonner";
import { type CertificateFormat, useEmailCertificate } from "../../../hooks/use-certificates";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { formatOptions } from "./certificate-formats";

interface CertificateEmailPanelProps {
  recordId: number;
  contactEmail: string | null;
  address: string | null;
  format: CertificateFormat;
  onSent: () => void;
  onCancel: () => void;
}

export function CertificateEmailPanel({
  recordId,
  contactEmail,
  address,
  format,
  onSent,
  onCancel,
}: CertificateEmailPanelProps): React.ReactElement {
  const emailCertificate = useEmailCertificate(String(recordId));

  const [emailTo, setEmailTo] = useState(contactEmail ?? "");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState(`Insurance Cost Estimate - ${address ?? ""}`);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailFormat, setEmailFormat] = useState<CertificateFormat>(format);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSendEmail = async (): Promise<void> => {
    setEmailError(null);
    const to = emailTo
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const cc = emailCc
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      await emailCertificate.mutateAsync({
        to,
        cc: cc.length > 0 ? cc : undefined,
        subject: emailSubject,
        message: emailMessage,
        format: emailFormat,
      });
      toast.success("Certificate emailed successfully");
      onSent();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send email";
      setEmailError(message);
      toast.error(message);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="email-to">To</Label>
        <Input
          id="email-to"
          value={emailTo}
          onChange={(e) => setEmailTo(e.target.value)}
          placeholder="name@example.com, name2@example.com"
          data-testid="email-to-input"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="email-cc">Cc</Label>
        <Input
          id="email-cc"
          value={emailCc}
          onChange={(e) => setEmailCc(e.target.value)}
          placeholder="name@example.com"
          data-testid="email-cc-input"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="email-subject">Subject</Label>
        <Input
          id="email-subject"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          data-testid="email-subject-input"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="email-format">Format</Label>
        <select
          id="email-format"
          value={emailFormat}
          onChange={(e) => setEmailFormat(e.target.value as CertificateFormat)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="email-format-select"
        >
          {formatOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="email-message">Message</Label>
        <Textarea
          id="email-message"
          value={emailMessage}
          onChange={(e) => setEmailMessage(e.target.value)}
          placeholder="Message to include in the email"
          data-testid="email-message-input"
        />
      </div>
      {emailError && (
        <p className="text-sm text-destructive" data-testid="email-error">
          {emailError}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="email-cancel-button">
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSendEmail}
          disabled={emailCertificate.isPending}
          data-testid="email-send-button"
        >
          {emailCertificate.isPending ? "Sending…" : "Send"}
        </Button>
      </div>
    </div>
  );
}
